import {
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Cascader,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useAddGroupMember,
  useAllModels,
  useAllSubSources,
  useDeleteModelGroup,
  useGroupMembers,
  useModelGroups,
  useProviders,
  useRemoveGroupMember,
  useResetGroupCooldown,
  useSaveModelGroup,
  useSetGroupDefaultModel,
  useUpdateGroupMember,
} from '../api/hooks';
import type { ModelGroup, ModelGroupMember, ProviderModel, SubSourceKind } from '../api/types';

// Nhan hien thi cho thao tac nguon con (Vilao).
const SUBSOURCE_KIND_LABEL: Record<SubSourceKind, string> = {
  chat: 'Chat',
  image: 'Ảnh (tạo / sửa)',
};
import { extractErrorMessage } from '../api/client';
import { CategorySelect } from '../components/CategorySelect';
import { jsonRule } from '../components/JsonField';
import { parseJsonText, toJsonText } from '../utils/format';
import { GroupCanonicalParams } from '../components/provider/GroupCanonicalParams';
import { GroupTestTab } from '../components/provider/GroupTestTab';

const { Title } = Typography;

/** Tạo slug từ tên: bỏ dấu tiếng Việt, thường hóa, thay khoảng trắng bằng dấu gạch. */
function slugify(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const EXEC_OPTIONS = [
  { value: 'gateway', label: 'Gateway' },
  { value: 'worker', label: 'Worker' },
  { value: 'hybrid', label: 'Kết hợp (Hybrid)' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Hoạt động' },
  { value: 'disabled', label: 'Đã tắt' },
];

export default function ModelGroupsPage() {
  const { message } = AntApp.useApp();
  const list = useModelGroups();
  const save = useSaveModelGroup();
  const del = useDeleteModelGroup();
  const resetCooldown = useResetGroupCooldown();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ModelGroup | null>(null);
  // Đánh dấu admin đã tự sửa slug => ngừng tự sinh slug từ tên.
  const [slugEdited, setSlugEdited] = useState(false);
  const [tab, setTab] = useState('config'); // tab modal: cấu hình | kiểm thử
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    setSlugEdited(false);
    setTab('config');
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      executionMode: 'hybrid',
      raceAllModels: false,
      overflowToNextPriority: false,
      overflowThreshold: 10,
      consecutiveErrorLimit: 5,
      cooldownMin1: 30,
      cooldownMin2: 360,
      cooldownMin3: 1440,
      groupRetryCount: 0,
    });
    setOpen(true);
  };

  const openEdit = (record: ModelGroup) => {
    setEditing(record);
    setSlugEdited(true); // sửa nhóm cũ: giữ nguyên slug, không tự sinh lại
    setTab('config');
    form.setFieldsValue({
      ...record,
      raceAllModels: record.raceAllModels ?? false,
      raceNeverStop: record.raceNeverStop ?? false,
      overflowToNextPriority: record.overflowToNextPriority ?? false,
      overflowThreshold: record.overflowThreshold ?? 10,
      consecutiveErrorLimit: record.consecutiveErrorLimit ?? 5,
      cooldownMin1: record.cooldownMin1 ?? 30,
      cooldownMin2: record.cooldownMin2 ?? 360,
      cooldownMin3: record.cooldownMin3 ?? 1440,
      groupRetryCount: record.groupRetryCount ?? 0,
      inputSchema: toJsonText(record.inputSchema),
      outputSchema: toJsonText(record.outputSchema),
    });
    setOpen(true);
  };

  // Tên đổi -> tự sinh slug (chỉ khi tạo mới & admin chưa tự sửa slug).
  const onValuesChange = (changed: Record<string, unknown>) => {
    if ('name' in changed && !slugEdited) {
      form.setFieldValue('slug', slugify(String(changed.name ?? '')));
    }
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    const input = parseJsonText(values.inputSchema ?? '');
    const output = parseJsonText(values.outputSchema ?? '');
    // Tham số chung (canonical) + ánh xạ được lưu riêng trong GroupCanonicalParams ("Lưu tham số nhóm").
    const payload: Partial<ModelGroup> = {
      slug: values.slug,
      name: values.name,
      category: values.category,
      description: values.description,
      status: values.status,
      executionMode: values.executionMode,
      raceAllModels: !!values.raceAllModels,
      raceNeverStop: !!values.raceNeverStop,
      overflowToNextPriority: !!values.overflowToNextPriority,
      overflowThreshold: Number(values.overflowThreshold ?? 10),
      consecutiveErrorLimit: Number(values.consecutiveErrorLimit ?? 5),
      cooldownMin1: Number(values.cooldownMin1 ?? 30),
      cooldownMin2: Number(values.cooldownMin2 ?? 360),
      cooldownMin3: Number(values.cooldownMin3 ?? 1440),
      groupRetryCount: Number(values.groupRetryCount ?? 0),
      inputSchema: input.value,
      outputSchema: output.value,
    };
    try {
      const saved = (await save.mutateAsync({ id: editing?.id, data: payload })) as ModelGroup;
      if (editing) {
        message.success('Đã cập nhật nhóm model');
        setOpen(false);
      } else {
        // Vừa tạo mới: GIỮ modal mở và chuyển sang chế độ sửa để chọn nhà cung cấp/model ngay.
        message.success('Đã tạo nhóm — giờ chọn nhà cung cấp & model bên dưới');
        setEditing(saved);
        setSlugEdited(true);
      }
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onDelete = async (id: string) => {
    try {
      await del.mutateAsync(id);
      message.success('Đã xóa nhóm model');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onResetCooldown = async (id: string, name: string) => {
    try {
      const res = await resetCooldown.mutateAsync(id);
      message.success(res.resetCount > 0
        ? `Đã reset tạm khóa ${res.resetCount} model con trong nhóm "${name}"`
        : `Nhóm "${name}" không có model nào đang bị tạm khóa`);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const columns = [
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Tên', dataIndex: 'name', key: 'name' },
    {
      title: 'Danh mục',
      dataIndex: 'category',
      key: 'category',
      render: (v: string) => v || '—',
    },
    {
      title: 'Chế độ',
      dataIndex: 'executionMode',
      key: 'executionMode',
      render: (v: string) => <Tag>{EXEC_OPTIONS.find((o) => o.value === v)?.label ?? v}</Tag>,
    },
    {
      title: 'Số model',
      dataIndex: 'memberCount',
      key: 'memberCount',
      render: (v: number) => v ?? 0,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) =>
        s === 'active' ? <Tag color="success">Hoạt động</Tag> : <Tag>Đã tắt</Tag>,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 280,
      render: (_: unknown, r: ModelGroup) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Sửa
          </Button>
          <Popconfirm
            title={`Reset tạm khóa cho tất cả model con trong nhóm "${r.name}"?`}
            okText="Reset"
            cancelText="Hủy"
            onConfirm={() => onResetCooldown(r.id, r.name)}
          >
            <Button size="small" loading={resetCooldown.isPending}>
              Reset tạm khóa
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Xóa nhóm model này?"
            okText="Xóa"
            cancelText="Hủy"
            onConfirm={() => onDelete(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={3} style={{ margin: 0 }}>
          Nhóm model
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Thêm nhóm model
        </Button>
      </Space>
      <Typography.Paragraph type="secondary">
        Gom các model từ <b>nhiều nhà cung cấp</b> vào một nhóm. Khi nguồn API lỗi, hệ thống tự
        động <b>xoay</b> sang model kế tiếp trong nhóm theo thứ tự ưu tiên (admin tự chọn thành
        viên, không tự động phân theo tên).
      </Typography.Paragraph>

      <Table<ModelGroup>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data ?? []}
        columns={columns}
      />

      <Modal
        title={editing ? 'Sửa nhóm model' : 'Thêm nhóm model'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        centered
        width="90vw"
        style={{ maxWidth: 1200 }}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto', paddingRight: 4 } }}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={save.isPending}
        destroyOnClose
      >
        {editing && (
          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
              { key: 'config', label: 'Cấu hình nhóm' },
              { key: 'test', label: 'Kiểm thử' },
            ]}
          />
        )}

        {editing && tab === 'test' && <GroupTestTab groupId={editing.id} />}

        <div style={{ display: editing && tab === 'test' ? 'none' : 'block' }}>
        <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
          <Space style={{ display: 'flex' }} align="start" size="large">
            <Form.Item
              name="name"
              label="Tên"
              rules={[{ required: true, message: 'Vui lòng nhập tên' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="vd: Ảnh ChatGPT" />
            </Form.Item>
            <Form.Item
              name="slug"
              label="Slug"
              tooltip="Tự sinh từ Tên. Có thể sửa thủ công nếu muốn."
              rules={[{ required: true, message: 'Vui lòng nhập slug' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="tu-sinh-tu-ten" onChange={() => setSlugEdited(true)} />
            </Form.Item>
          </Space>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start" size="large">
            <Form.Item name="category" label="Danh mục" style={{ flex: 1 }}>
              <CategorySelect />
            </Form.Item>
            <Form.Item name="status" label="Trạng thái" style={{ flex: 1 }}>
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
            <Form.Item name="executionMode" label="Chế độ thực thi" style={{ flex: 1 }}>
              <Select options={EXEC_OPTIONS} />
            </Form.Item>
          </Space>

          {/* Tạo job tất cả model (race-to-first) + Không ngừng job */}
          <Space size="large" align="start">
            <Form.Item
              name="raceAllModels"
              label="Tạo job tất cả model"
              valuePropName="checked"
              tooltip="Bật: khi có request, tạo ĐỒNG THỜI tất cả model con (trừ backup + cooldown + bị tắt riêng). Model nào trả kết quả sớm nhất thì dùng kết quả đó, các model còn lại bị hủy. Mục đích: đảm bảo luôn có kết quả nhanh nhất."
            >
              <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
            </Form.Item>
            <Form.Item
              name="raceNeverStop"
              label="Không ngừng job"
              valuePropName="checked"
              tooltip="Chỉ có tác dụng khi 'Tạo job tất cả model' bật. Bật: model con thắng KHÔNG hủy các model con khác — tất cả chạy đến khi thành công hoặc lỗi. Mục đích: kiểm tra độ ổn định khi tạo của từng model con."
            >
              <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
            </Form.Item>
          </Space>

          {/* Tràn danh sách chờ (rate limit) sang ưu tiên kế tiếp. */}
          <Space style={{ display: 'flex' }} align="start" size="large">
            <Form.Item
              name="overflowToNextPriority"
              label="Đẩy danh sách chờ sang ưu tiên kế tiếp"
              valuePropName="checked"
              tooltip="Khi model ưu tiên đầu đạt giới hạn (request/phút như ZORA, hoặc số request đồng thời như GPT Playwright), request mới sẽ vào danh sách chờ. Bật tùy chọn này để duyệt LẦN LƯỢT các model ưu tiên kế tiếp và chạy trên model đầu tiên còn slot trống. Nếu TẤT CẢ model trong nhóm đều đầy, request vào danh sách chờ của nhóm và tự chạy ngay khi bất kỳ model con nào trống. Cơ chế xoay khi LỖI vẫn giữ nguyên."
              style={{ flex: 1 }}
            >
              <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(p, c) => p.overflowToNextPriority !== c.overflowToNextPriority}
            >
              {({ getFieldValue }) => (
                <Form.Item
                  name="overflowThreshold"
                  label="Ngưỡng danh sách chờ"
                  tooltip="Chỉ đẩy sang ưu tiên kế tiếp khi số lượng đang chờ đạt ngưỡng này trở lên. Ví dụ ngưỡng 10: giữ tối đa 10 request trong danh sách chờ, từ request thứ 11 trở đi mới đẩy sang ưu tiên kế tiếp."
                  style={{ flex: 1 }}
                >
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    disabled={!getFieldValue('overflowToNextPriority')}
                    placeholder="10"
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Space>

          {/* Circuit breaker: tạm khóa model con khi lỗi liên tiếp + cooldown leo thang. */}
          <Divider orientation="left" plain>
            Tạm khóa model con khi lỗi (circuit breaker)
          </Divider>
          <Space style={{ display: 'flex' }} align="start" size="large" wrap>
            <Form.Item
              name="consecutiveErrorLimit"
              label="Số lỗi liên tiếp để tạm khóa"
              tooltip="Khi 1 model con lỗi liên tiếp đủ số lần này, model đó bị tạm khóa và nhóm tự xoay sang model ưu tiên kế tiếp."
              style={{ flex: 1, minWidth: 180 }}
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="5" />
            </Form.Item>
            <Form.Item
              name="cooldownMin1"
              label="Cooldown bậc 1 (phút)"
              tooltip="Tạm khóa lần đầu (mặc định 30 phút). Có thông báo cho admin ở Tổng quan."
              style={{ flex: 1, minWidth: 150 }}
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="30" />
            </Form.Item>
            <Form.Item
              name="cooldownMin2"
              label="Cooldown bậc 2 (phút)"
              tooltip="Hết bậc 1, gọi lại mà còn lỗi → tạm khóa lâu hơn (mặc định 360 = 6 giờ)."
              style={{ flex: 1, minWidth: 150 }}
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="360" />
            </Form.Item>
            <Form.Item
              name="cooldownMin3"
              label="Cooldown bậc 3 (phút)"
              tooltip="Leo thang tiếp (mặc định 1440 = 24 giờ); giữ ở bậc cao nhất cho đến khi model chạy lại thành công."
              style={{ flex: 1, minWidth: 150 }}
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="1440" />
            </Form.Item>
            <Form.Item
              name="groupRetryCount"
              label="Số lần tạo lại khi lỗi (cả nhóm)"
              tooltip="Khi TẤT CẢ model con trong nhóm đều lỗi (đã xoay hết), nhóm sẽ xóa danh sách đã thử + mở khóa cooldown rồi chạy lại từ model ưu tiên cao nhất, tối đa số lần này. 0 = tắt (báo lỗi ngay như trước). Không áp dụng cho chế độ 'Tạo job tất cả model'."
              style={{ flex: 1, minWidth: 180 }}
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
            </Form.Item>
          </Space>

          {/* Khu vực chọn model trong nhóm — đặt TRÊN Input/Output Schema để dễ thao tác. */}
          <Divider />
          {editing ? (
            <GroupMembers groupId={editing.id} />
          ) : (
            <Typography.Text type="secondary">
              Nhấn <b>Lưu</b> để tạo nhóm, sau đó chọn <b>nhà cung cấp → model</b> và <b>model mặc
              định</b> ngay tại đây.
            </Typography.Text>
          )}
          <Divider />

          <Form.Item name="inputSchema" label="Input Schema (JSON)" rules={[jsonRule]}>
            <Input.TextArea rows={4} style={{ fontFamily: 'monospace' }} placeholder="{ }" />
          </Form.Item>
          <Form.Item name="outputSchema" label="Output Schema (JSON)" rules={[jsonRule]}>
            <Input.TextArea rows={4} style={{ fontFamily: 'monospace' }} placeholder="{ }" />
          </Form.Item>
        </Form>
        </div>
      </Modal>
    </div>
  );
}

// --- Thành viên của nhóm: chọn nhà cung cấp -> (danh mục) -> model con cuối, kéo-thả ưu tiên ---
function GroupMembers({ groupId }: { groupId: string }) {
  const { message } = AntApp.useApp();
  const members = useGroupMembers(groupId);
  const allModels = useAllModels();
  const allSubSources = useAllSubSources();
  const providers = useProviders();
  const groups = useModelGroups();
  const add = useAddGroupMember(groupId);
  const update = useUpdateGroupMember(groupId);
  const remove = useRemoveGroupMember(groupId);
  const setDefault = useSetGroupDefaultModel(groupId);

  // Model mặc định + bản ghi nhóm lấy từ cache (tự cập nhật sau khi đổi).
  const groupRow = groups.data?.find((g) => g.id === groupId);
  const defaultModelId = groupRow?.defaultModelId ?? null;

  // Đường dẫn chọn trong Cascader: [providerId, (catKey), modelId]. Leaf = modelId.
  const [pickPath, setPickPath] = useState<string[]>([]);
  const pickedModelId = pickPath.length ? pickPath[pickPath.length - 1] : undefined;

  // Thứ tự cục bộ để kéo-thả mượt; đồng bộ lại mỗi khi server trả về.
  const [order, setOrder] = useState<ModelGroupMember[]>([]);
  const [editingMember, setEditingMember] = useState<ModelGroupMember | null>(null);
  const [memberForm] = Form.useForm();

  useEffect(() => {
    setOrder((members.data ?? []).slice().sort((a, b) => a.priority - b.priority));
  }, [members.data]);

  useEffect(() => {
    if (editingMember) {
      memberForm.setFieldsValue({
        overrideConcurrent: editingMember.overrideConcurrent,
        overrideRateLimit: editingMember.overrideRateLimit,
        useProviderRetryCount: editingMember.useProviderRetryCount ?? true,
        retryCountOverride: editingMember.retryCountOverride,
        onlyOnError: editingMember.onlyOnError ?? false,
        allowRaceAll: editingMember.allowRaceAll ?? true,
      });
    }
  }, [editingMember, memberForm]);

  const onSaveMember = async () => {
    if (!editingMember) return;
    try {
      const values = await memberForm.validateFields();
      await update.mutateAsync({
        memberId: editingMember.id,
        body: {
          overrideConcurrent: values.overrideConcurrent ? Number(values.overrideConcurrent) : null,
          overrideRateLimit: values.overrideRateLimit ? Number(values.overrideRateLimit) : null,
          useProviderRetryCount: values.useProviderRetryCount ?? true,
          retryCountOverride: values.useProviderRetryCount ? null : Number(values.retryCountOverride ?? 0),
          onlyOnError: !!values.onlyOnError,
          allowRaceAll: values.allowRaceAll ?? true,
        },
      });
      message.success('Đã cập nhật cấu hình model con');
      setEditingMember(null);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  // Cây Cascader: Nhà cung cấp → Danh mục (nếu có) → Model (lá = model con cuối cùng).
  // CHO PHÉP TRÙNG LẬp (cùng model/provider) — không lọc bỏ model đã nằm trong nhóm.
  const cascaderOptions = useMemo(() => {
    // Vilao / GPT Smart Provider: CHỈ dùng nguồn con — bỏ nhánh model khỏi cây.
    const usable = (allModels.data ?? []).filter(
      (m) => m.provider && m.provider.adapter !== 'vilao' && m.provider.adapter !== 'gpt-smart',
    );

    const byProvider = new Map<string, ProviderModel[]>();
    for (const m of usable) {
      const pid = m.provider!.id;
      if (!byProvider.has(pid)) byProvider.set(pid, []);
      byProvider.get(pid)!.push(m);
    }

    return (providers.data ?? [])
      .filter((p) => (byProvider.get(p.id)?.length ?? 0) > 0)
      .map((p) => {
        const models = byProvider.get(p.id) ?? [];
        const byCat = new Map<string, ProviderModel[]>();
        const noCat: ProviderModel[] = [];
        for (const m of models) {
          const c = (m.category ?? '').trim();
          if (c) {
            if (!byCat.has(c)) byCat.set(c, []);
            byCat.get(c)!.push(m);
          } else {
            noCat.push(m);
          }
        }
        const children: Array<{
          value: string;
          label: string;
          children?: Array<{ value: string; label: string }>;
        }> = [];
        for (const [cat, ms] of byCat) {
          children.push({
            value: `cat:${p.id}:${cat}`,
            label: cat,
            children: ms.map((m) => ({ value: m.id, label: `${m.name} (${m.code})` })),
          });
        }
        for (const m of noCat) {
          children.push({ value: m.id, label: `${m.name} (${m.code})` });
        }
        return { value: p.id, label: `${p.name} (${p.slug})`, children };
      });
  }, [providers.data, allModels.data, members.data]);

  // Nhánh NGUỒN CON theo provider: Nguồn con → Thao tác (chat/tạo ảnh/sửa ảnh).
  // Lá mã hoá `sub:<subSourceId>:<kind>`. Bỏ tổ hợp (nguồn con + thao tác) đã thêm.
  const subChildrenByProvider = useMemo(() => {
    const memberSubKeys = new Set(
      (members.data ?? [])
        .filter((m) => m.subSourceId)
        .map((m) => `${m.subSourceId}:${m.modelKind ?? 'chat'}`),
    );
    const byProvider = new Map<string, any[]>();
    for (const s of allSubSources.data ?? []) {
      if (!s.enabled || !s.provider || s.provider.status !== 'active') continue;
      const kinds: SubSourceKind[] = [];
      if (s.chatModel) kinds.push('chat');
      // Ảnh dùng chung 1 thao tác (tạo & sửa). imageModel ưu tiên; editImageModel là dữ liệu cũ.
      if (s.imageModel || s.editImageModel) kinds.push('image');
      const node = {
        value: `subp:${s.id}`,
        label: s.name,
        children: kinds
          .filter((k) => !memberSubKeys.has(`${s.id}:${k}`))
          .map((k) => ({ value: `sub:${s.id}:${k}`, label: SUBSOURCE_KIND_LABEL[k] })),
      };
      const pid = s.provider.id;
      if (!byProvider.has(pid)) byProvider.set(pid, []);
      byProvider.get(pid)!.push(node);
    }
    return byProvider;
  }, [allSubSources.data, members.data]);

  // Gộp model + nguồn con vào CÙNG 1 node nhà cung cấp. Nguồn con hiển thị TRỰC TIẾP dưới
  // node nhà cung cấp (vd GPT Smart Provider). Provider chỉ có nguồn con vẫn hiện đầy đủ.
  const allCascaderOptions = useMemo(() => {
    // Gắn nguồn con TRỰC TIẾP dưới node nhà cung cấp (không bọc thêm cấp 'Nguồn con'):
    // rê chuột vào GPT Smart Provider là thấy ngay danh sách nguồn con.
    const merged = cascaderOptions.map((opt) => {
      const subs = subChildrenByProvider.get(opt.value);
      if (!subs?.length) return opt;
      return { ...opt, children: [...(opt.children ?? []), ...subs] };
    });
    // Provider chỉ có nguồn con (chưa xuất hiện ở cascaderOptions vì không có model).
    const seen = new Set(cascaderOptions.map((o) => o.value));
    for (const [pid, subs] of subChildrenByProvider) {
      if (seen.has(pid) || !subs.length) continue;
      const p = (providers.data ?? []).find((x) => x.id === pid);
      merged.push({
        value: pid,
        label: p ? `${p.name} (${p.slug})` : 'Nhà cung cấp',
        children: subs,
      } as any);
    }
    return merged;
  }, [cascaderOptions, subChildrenByProvider, providers.data]);

  const onAdd = async () => {
    if (!pickedModelId || pickedModelId.startsWith('cat:') || pickedModelId.startsWith('subp:') || pickedModelId.startsWith('subgrp:')) {
      message.warning('Vui lòng chọn tới model / thao tác nguồn con cuối cùng');
      return;
    }
    try {
      const nextPriority = (order.length + 1) * 10;
      if (pickedModelId.startsWith('sub:')) {
        // sub:<subSourceId>:<kind>
        const [, subSourceId, kind] = pickedModelId.split(':');
        await add.mutateAsync({ subSourceId, modelKind: kind as SubSourceKind, priority: nextPriority });
        message.success('Đã thêm nguồn con vào nhóm');
      } else {
        await add.mutateAsync({ modelId: pickedModelId, priority: nextPriority });
        message.success('Đã thêm model vào nhóm');
      }
      setPickPath([]);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const onToggleDefault = async (r: ModelGroupMember) => {
    const next = defaultModelId === r.modelId ? null : (r.modelId ?? null);
    try {
      await setDefault.mutateAsync(next);
      message.success(next ? 'Đã đặt model mặc định' : 'Đã bỏ model mặc định');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  // Ghi lại thứ tự ưu tiên mới (10, 20, 30…) cho các dòng đã đổi vị trí.
  const persistOrder = async (ordered: ModelGroupMember[]) => {
    try {
      await Promise.all(
        ordered.map((m, i) => {
          const np = (i + 1) * 10;
          return m.priority === np
            ? Promise.resolve()
            : update.mutateAsync({ memberId: m.id, body: { priority: np } });
        }),
      );
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  // --- Kéo-thả đổi thứ tự ưu tiên ---
  const dragIndex = useRef<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const handleDrop = (dropIdx: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    setOverIndex(null);
    if (from === null || from === dropIdx) return;
    const next = order.slice();
    const [moved] = next.splice(from, 1);
    next.splice(dropIdx, 0, moved);
    setOrder(next);
    void persistOrder(next);
  };

  const columns = [
    {
      title: '',
      key: 'drag',
      width: 44,
      align: 'center' as const,
      render: () => (
        <Tooltip title="Kéo để đổi thứ tự ưu tiên">
          <HolderOutlined style={{ cursor: 'grab', color: '#999' }} />
        </Tooltip>
      ),
    },
    {
      title: 'Mặc định',
      key: 'default',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, r: ModelGroupMember) => {
        // Nguồn con không hỗ trợ "model mặc định" (default dựa trên modelId).
        if (!r.modelId) return <span style={{ color: '#ccc' }}>—</span>;
        const isDefault = defaultModelId === r.modelId;
        return (
          <Tooltip
            title={isDefault ? 'Đang là model mặc định (thử trước tiên)' : 'Đặt làm model mặc định'}
          >
            <Button
              size="small"
              type="text"
              loading={setDefault.isPending}
              icon={isDefault ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
              onClick={() => onToggleDefault(r)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Ưu tiên',
      key: 'priority',
      width: 70,
      align: 'center' as const,
      render: (_: unknown, __: ModelGroupMember, index: number) => (
        <Tag>{index + 1}</Tag>
      ),
    },
    {
      title: 'Model (Nhà cung cấp · Model / Nguồn con)',
      key: 'model',
      render: (_: unknown, r: ModelGroupMember) => {
        if (r.subSource) {
          return (
            <span>
              {r.subSource.provider?.name ?? '?'} · {r.subSource.name}{' '}
              <Tag color="purple" style={{ marginLeft: 4 }}>
                nguồn con · {SUBSOURCE_KIND_LABEL[(r.modelKind as SubSourceKind) ?? 'chat']}
              </Tag>
            </span>
          );
        }
        return r.model ? (
          <span>
            {r.model.provider?.name ?? '?'} · {r.model.name}{' '}
            <Tag style={{ marginLeft: 4 }}>{r.model.code}</Tag>
            {defaultModelId === r.modelId && (
              <Tag color="gold" style={{ marginLeft: 4 }}>
                Mặc định
              </Tag>
            )}
          </span>
        ) : (
          r.modelId
        );
      },
    },
    {
      title: 'Race',
      key: 'allowRaceAll',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, r: ModelGroupMember) => (
        <Tooltip title={r.allowRaceAll !== false ? 'Tham gia khi nhóm bật "Tạo job tất cả model"' : 'Bỏ qua trong chế độ race'}>
          <Button
            size="small"
            type={r.allowRaceAll !== false ? 'primary' : 'default'}
            onClick={() => update.mutate({ memberId: r.id, body: { allowRaceAll: r.allowRaceAll === false ? true : false } })}
          >
            {r.allowRaceAll !== false ? 'Có' : 'Không'}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Chỉ khi lỗi',
      key: 'onlyOnError',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, r: ModelGroupMember) => (
        <Tooltip title={r.onlyOnError ? 'Chỉ chạy khi các model phía trên LỖI (backup)' : 'Đang chạy bình thường (không phải backup)'}>
          <Button
            size="small"
            type={r.onlyOnError ? 'primary' : 'default'}
            danger={r.onlyOnError}
            onClick={() => update.mutate({ memberId: r.id, body: { onlyOnError: !r.onlyOnError } })}
          >
            {r.onlyOnError ? 'Backup' : '—'}
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Bật',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled: boolean, r: ModelGroupMember) => (
        <Button
          size="small"
          type={enabled ? 'primary' : 'default'}
          onClick={() => update.mutate({ memberId: r.id, body: { enabled: !enabled } })}
        >
          {enabled ? 'Bật' : 'Tắt'}
        </Button>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 100,
      render: (_: unknown, r: ModelGroupMember) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditingMember(r)}>
            Sửa
          </Button>
          <Popconfirm
            title="Gỡ model khỏi nhóm?"
            okText="Gỡ"
            cancelText="Hủy"
            onConfirm={() => remove.mutate(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={5}>Model trong nhóm</Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Chọn <b>nhà cung cấp → (danh mục) → model con cuối cùng</b>, hoặc <b>nguồn con → thao tác</b>{' '}
        (Vilao). Có thể thêm CÙNG nhà cung cấp và cùng model nhiều lần (ví dụ: để cấu hình backup). Khi có
        request, nhóm thử <b>model mặc định</b> (⭐) trước, rồi xoay theo <b>thứ tự ưu tiên</b>{' '}
        (kéo-thả để sắp xếp). Model được đánh dấu <b>"Chỉ khi lỗi"</b> chỉ chạy khi các model phía trên{' '}
        <b>lỗi thật sự</b>, không chạy khi chỉ hết slot (overflow).
      </Typography.Paragraph>
      <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
        <Cascader
          style={{ flex: 1 }}
          placeholder="Chọn nhà cung cấp → danh mục → model con cuối (hoặc nguồn con → thao tác)"
          value={pickPath}
          onChange={(v) => setPickPath((v as string[]) ?? [])}
          options={allCascaderOptions}
          loading={providers.isLoading || allModels.isLoading || allSubSources.isLoading}
          showSearch={{
            filter: (inputValue, path) =>
              path.some((opt) =>
                String(opt.label).toLowerCase().includes(inputValue.toLowerCase()),
              ),
          }}
          changeOnSelect={false}
          expandTrigger="hover"
        />
        <Button type="primary" icon={<PlusOutlined />} loading={add.isPending} onClick={onAdd}>
          Thêm
        </Button>
      </Space.Compact>
      <Table<ModelGroupMember>
        rowKey="id"
        size="small"
        loading={members.isLoading}
        dataSource={order}
        columns={columns}
        pagination={false}
        onRow={(_, index) => ({
          draggable: true,
          onDragStart: () => {
            dragIndex.current = index ?? null;
          },
          onDragOver: (e) => {
            e.preventDefault();
            setOverIndex(index ?? null);
          },
          onDragEnd: () => {
            dragIndex.current = null;
            setOverIndex(null);
          },
          onDrop: () => handleDrop(index ?? 0),
          style:
            overIndex === index
              ? { borderTop: '2px solid #1677ff', cursor: 'grab' }
              : { cursor: 'grab' },
        })}
      />

      <Divider />
      <Title level={5}>Tham số chung của nhóm (canonical) — cho phần mềm thứ 3</Title>
      <GroupCanonicalParams groupId={groupId} />

      <Modal
        title="Cấu hình model con"
        open={!!editingMember}
        onCancel={() => setEditingMember(null)}
        onOk={onSaveMember}
        centered
        okText="Lưu"
        cancelText="Hủy"
        destroyOnClose
        confirmLoading={update.isPending}
      >
        <Form form={memberForm} layout="vertical">
          <Typography.Paragraph type="secondary">
            Cài đặt này có thể <b>ghi đè</b> giới hạn và số lần tạo lại của nhà cung cấp. Nếu để mặc định,
            model con dùng cấu hình gốc của nhà cung cấp.
          </Typography.Paragraph>
          <Form.Item
            name="onlyOnError"
            label="Chỉ chạy khi lỗi (Backup)"
            tooltip="Bật: model này CHỈ chạy khi các model phía trên LỖI (failover). Nếu các model phía trên hết slot (overflow/danh sách chờ) nhưng không lỗi, model này sẽ bị bỏ qua."
            valuePropName="checked"
          >
            <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
          </Form.Item>
          <Form.Item
            name="allowRaceAll"
            label="Cho phép tạo job tất cả model"
            tooltip="Bật: member này sẽ tham gia khi nhóm bật chế độ 'Tạo job tất cả model'. Tắt để bỏ qua member này trong chế độ race."
            valuePropName="checked"
          >
            <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
          </Form.Item>
          <Form.Item
            name="overrideRateLimit"
            label="Giới hạn request/phút"
            tooltip="Ghi đè cấu hình Rate limit. Để trống để dùng mặc định."
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="vd: 10 (trống = mặc định)" />
          </Form.Item>
          <Form.Item
            name="overrideConcurrent"
            label="Số request đồng thời tối đa"
            tooltip="Ghi đè cấu hình Concurrency limit. Để trống để dùng mặc định."
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="vd: 3 (trống = mặc định)" />
          </Form.Item>
          <Form.Item
            name="useProviderRetryCount"
            label="Số lần tạo lại khi lỗi mặc định"
            tooltip="Bật: dùng số lần tạo lại khi lỗi đã cấu hình ở nhà cung cấp. Tắt: nhập số riêng cho model con trong nhóm này."
            valuePropName="checked"
          >
            <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.useProviderRetryCount !== c.useProviderRetryCount}>
            {({ getFieldValue }) => (
              <Form.Item
                name="retryCountOverride"
                label="Số lần tạo lại khi lỗi"
                tooltip="Áp dụng riêng cho model con trong nhóm khi không dùng mặc định của nhà cung cấp."
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  disabled={getFieldValue('useProviderRetryCount') !== false}
                  placeholder="0"
                />
              </Form.Item>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
